// Phase COTTON-RM-002 Part B — Cotton Cost Adjustments admin.
//
// Tabs: Pending Approval · By GRN · Landed Cost Summary · Downstream
// Warnings. Adjustments add/subtract from the GRN's landed cost and
// recompute Bale.effective_cost_per_kg on non-consumed bales.

import { useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Col, Empty, Form, Input, InputNumber, Modal,
  Row, Select, Skeleton, Space, Statistic, Table, Tabs, Tag, Typography,
} from 'antd'
import {
  CheckCircleOutlined, PlusOutlined, ReloadOutlined, WarningOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import apiClient from '@/shared/api/client'
import {
  ADJUSTMENT_TYPES, cottonRmApi, type CostAdjustment, type GrnLandedSummary,
} from '@/features/cotton-rm/cottonRmApi'

const { Text, Title } = Typography

function statusColor(s: string) {
  if (s === 'APPROVED') return 'success'
  if (s === 'PENDING') return 'warning'
  if (s === 'REJECTED') return 'error'
  if (s === 'POSTED') return 'blue'
  return 'default'
}

function confidenceColor(c: string) {
  if (c === 'EXACT') return 'success'
  if (c === 'INFERRED') return 'blue'
  if (c === 'ESTIMATED') return 'warning'
  if (c === 'PROVISIONAL') return 'gold'
  return 'default'
}


function CreateAdjModal({ open, onClose, onCreated }: {
  open: boolean; onClose: () => void; onCreated: () => void
}) {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const [plantId, setPlantId] = useState<string | undefined>()

  const { data: plants = [] } = useQuery({
    queryKey: ['plants-for-adj'],
    queryFn: async () => (await apiClient.get<{ id: string; plant_name: string }[]>('/plants')).data,
  })
  const { data: grns = [] } = useQuery({
    queryKey: ['grns-for-adj', plantId],
    queryFn: () => apiClient.get<{ id: string; grn_no: string }[]>('/grn',
      { params: { plant_id: plantId, limit: 200 } }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (vals: Record<string, unknown>) => cottonRmApi.createAdj(vals as never),
    onSuccess: () => {
      message.success('Adjustment created (pending approval).')
      onCreated()
      onClose()
      form.resetFields()
    },
    onError: (e: Error) => message.error(e.message),
  })

  return (
    <Modal open={open} title="New Cost Adjustment" onCancel={onClose}
            width={680}
            onOk={() => form.validateFields().then(vals => createMut.mutate(vals))}
            confirmLoading={createMut.isPending}>
      <Form form={form} layout="vertical">
        <Select placeholder="Plant" style={{ marginBottom: 8 }}
                value={plantId} onChange={setPlantId}
                options={plants.map(p => ({ value: p.id, label: p.plant_name }))} />
        <Form.Item name="grn_id" label="GRN" rules={[{ required: true }]}>
          <Select placeholder="Pick GRN" showSearch optionFilterProp="label"
                  options={grns.map(g => ({ value: g.id, label: g.grn_no }))} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="adjustment_type" label="Type" rules={[{ required: true }]}>
              <Select options={ADJUSTMENT_TYPES.map(t => ({ value: t }))} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="adjustment_basis" label="Basis"
                       initialValue="FLAT">
              <Select options={[
                { value: 'FLAT' }, { value: 'PER_KG' },
                { value: 'PER_BALE' }, { value: 'PERCENT' },
              ]} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="amount" label="Amount ₹" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="sign" label="Sign" initialValue={1}>
              <Select options={[
                { value: 1, label: '+ adds to cost' },
                { value: -1, label: '− reduces cost (discount)' },
              ]} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="calculation_confidence" label="Confidence"
                       initialValue="EXACT">
              <Select options={[
                { value: 'EXACT' }, { value: 'ESTIMATED' },
                { value: 'INFERRED' }, { value: 'PROVISIONAL' },
                { value: 'DATA_NOT_AVAILABLE' },
              ]} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
          <Input.TextArea rows={3} placeholder="Why this adjustment?" />
        </Form.Item>
      </Form>
    </Modal>
  )
}


function PendingTab() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['adj-pending'],
    queryFn: () => cottonRmApi.pendingAdj(),
  })
  const approveMut = useMutation({
    mutationFn: (id: string) => cottonRmApi.approveAdj(id),
    onSuccess: () => {
      message.success('Approved — bale costs recomputed.')
      qc.invalidateQueries({ queryKey: ['adj-pending'] })
      qc.invalidateQueries({ queryKey: ['adj-by-grn'] })
    },
    onError: (e: Error) => message.error(e.message),
  })

  const cols = [
    { title: 'Type', dataIndex: 'adjustment_type',
      render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Amount', dataIndex: 'amount',
      render: (n: number, r: CostAdjustment) =>
        `${r.sign < 0 ? '−' : '+'}₹${n.toLocaleString()}` },
    { title: 'Confidence', dataIndex: 'calculation_confidence',
      render: (c: string) => <Tag color={confidenceColor(c)}>{c}</Tag> },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true },
    { title: 'Downstream', dataIndex: 'downstream_warning',
      render: (w: string | null) => w ? (
        <Tag color="warning" icon={<WarningOutlined />}>
          Consumed-bale warning
        </Tag>
      ) : '' },
    { title: '', key: 'action',
      render: (_: unknown, r: CostAdjustment) => (
        <Button size="small" type="primary"
                onClick={() => approveMut.mutate(r.id)}
                loading={approveMut.isPending}
                icon={<CheckCircleOutlined />}>
          Approve
        </Button>
      ) },
  ]
  return (
    <Card extra={<Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>}>
      {isLoading ? <Skeleton active /> :
        rows.length === 0 ? <Empty description="No pending adjustments." /> : (
          <Table rowKey="id" columns={cols as never} dataSource={rows}
                  size="small" pagination={{ pageSize: 30 }} />
        )}
    </Card>
  )
}


function ByGrnTab() {
  const [plantId, setPlantId] = useState<string | undefined>()
  const [grnId, setGrnId] = useState<string | undefined>()
  const { data: plants = [] } = useQuery({
    queryKey: ['plants-for-grn-adj'],
    queryFn: async () => (await apiClient.get<{ id: string; plant_name: string }[]>('/plants')).data,
  })
  const { data: grns = [] } = useQuery({
    queryKey: ['grns-for-grn-adj', plantId],
    queryFn: () => apiClient.get<{ id: string; grn_no: string }[]>('/grn',
      { params: { plant_id: plantId, limit: 200 } }).then(r => r.data),
    enabled: !!plantId,
  })
  const { data: summary, isLoading } = useQuery<GrnLandedSummary>({
    queryKey: ['adj-by-grn', grnId],
    queryFn: () => cottonRmApi.grnLandedSummary(grnId!),
    enabled: !!grnId,
  })

  const cols = [
    { title: 'Type', dataIndex: 'adjustment_type',
      render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Amount', dataIndex: 'amount',
      render: (n: number, r: CostAdjustment) =>
        `${r.sign < 0 ? '−' : '+'}₹${n.toLocaleString()}` },
    { title: 'Status', dataIndex: 'approval_status',
      render: (s: string) => <Tag color={statusColor(s)}>{s}</Tag> },
    { title: 'Confidence', dataIndex: 'calculation_confidence',
      render: (c: string) => <Tag color={confidenceColor(c)}>{c}</Tag> },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true },
    { title: 'Created', dataIndex: 'created_at',
      render: (s: string) => new Date(s).toLocaleString() },
  ]

  return (
    <Card>
      <Space wrap style={{ marginBottom: 12 }}>
        <Select placeholder="Plant" style={{ width: 220 }}
                value={plantId} onChange={setPlantId}
                options={plants.map(p => ({ value: p.id, label: p.plant_name }))} />
        <Select placeholder="GRN" style={{ width: 240 }}
                value={grnId} onChange={setGrnId}
                showSearch optionFilterProp="label"
                options={grns.map(g => ({ value: g.id, label: g.grn_no }))} />
      </Space>
      {!grnId ? <Empty description="Pick a GRN." /> :
        isLoading ? <Skeleton active /> :
        summary && (
          <>
            <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
              <Col span={6}><Card size="small">
                <Statistic title="Base invoice"
                  value={summary.base_invoice_amount ?? 0}
                  prefix="₹" /></Card></Col>
              <Col span={6}><Card size="small">
                <Statistic title="Approved adjustments"
                  value={summary.approved_adjustment_total}
                  prefix="₹"
                  valueStyle={{ color: summary.approved_adjustment_total >= 0 ? '#52c41a' : '#ff4d4f' }} /></Card></Col>
              <Col span={6}><Card size="small">
                <Statistic title="Pending adjustments"
                  value={summary.pending_adjustment_total}
                  prefix="₹"
                  valueStyle={{ color: '#faad14' }} /></Card></Col>
              <Col span={6}><Card size="small">
                <Statistic title="Avg bale ₹/kg (current)"
                  value={summary.current_avg_bale_cost_per_kg ?? 0}
                  prefix="₹" precision={2} /></Card></Col>
            </Row>
            <Table rowKey="id" columns={cols as never}
                    dataSource={summary.adjustments}
                    size="small" pagination={false} />
          </>
        )}
    </Card>
  )
}


function DownstreamTab() {
  const { data: rows = [] } = useQuery({
    queryKey: ['adj-pending'],
    queryFn: () => cottonRmApi.pendingAdj(),
  })
  const withWarning = rows.filter(r => r.downstream_warning)
  return (
    <Card>
      <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message="Adjustments below touch bales that are already ISSUED or CONSUMED."
              description="Their historical cost is NOT silently restated. Use the Part C restatement workflow with finance approval." />
      {withWarning.length === 0 ? <Empty description="No downstream warnings." /> : (
        <Table rowKey="id" size="small" pagination={false}
                columns={[
                  { title: 'Type', dataIndex: 'adjustment_type',
                    render: (v: string) => <Tag>{v}</Tag> },
                  { title: 'Amount', dataIndex: 'amount',
                    render: (n: number, r: CostAdjustment) =>
                      `${r.sign < 0 ? '−' : '+'}₹${n.toLocaleString()}` },
                  { title: 'Warning', dataIndex: 'downstream_warning',
                    render: (w: string) => <Text type="warning">{w}</Text> },
                ] as never}
                dataSource={withWarning} />
      )}
    </Card>
  )
}


export default function CottonCostAdjustmentsPage() {
  const [createOpen, setCreateOpen] = useState(false)
  const qc = useQueryClient()
  return (
    <div style={{ padding: 16, maxWidth: 1500, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Cotton Cost Adjustments</Title>
      <Text type="secondary">
        Capture freight, brokerage, weighbridge, QC, storage, insurance,
        moisture/quality discounts, and supplier claims. Approved
        adjustments recompute the GRN's landed cost and roll it down to
        non-consumed bales. Consumed bales raise a downstream warning
        instead of silent restatement.
      </Text>

      <Space style={{ marginTop: 12 }}>
        <Button type="primary" icon={<PlusOutlined />}
                 onClick={() => setCreateOpen(true)}>
          New Adjustment
        </Button>
      </Space>

      <Tabs style={{ marginTop: 16 }}
            items={[
              { key: 'pending', label: 'Pending Approval', children: <PendingTab /> },
              { key: 'by-grn',  label: 'By GRN + Landed Summary', children: <ByGrnTab /> },
              { key: 'downstream', label: 'Downstream Warnings', children: <DownstreamTab /> },
            ]} />

      <CreateAdjModal open={createOpen}
                       onClose={() => setCreateOpen(false)}
                       onCreated={() => {
                         qc.invalidateQueries({ queryKey: ['adj-pending'] })
                       }} />
    </div>
  )
}
